"use client";

import { useState, useEffect } from "react";
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
} from "@/components/ui/context-menu";
import { DraggableContextMenuItem } from "./tier-list/DraggableContextMenuItem";

type ItemPickerContextMenuProps = {
  children: React.ReactNode;
  items: string[];
  itemNames: Record<string, string>;
  imageUrls: Record<string, string>;
  onItemSelect: (itemId: string) => void;
  enabled: boolean;
  activeId?: string | number | null;
};

export function ItemPickerContextMenu({
  children,
  items,
  itemNames,
  imageUrls,
  onItemSelect,
  enabled,
  activeId,
}: ItemPickerContextMenuProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Auto-close menu when any drag operation starts
  useEffect(() => {
    if (activeId !== null && activeId !== undefined) {
      setIsOpen(false);
    }
  }, [activeId, setIsOpen]);

  if (!enabled) {
    return <>{children}</>;
  }

  return (
    <ContextMenu onOpenChange={setIsOpen}>
      <ContextMenuTrigger className="contents">{children}</ContextMenuTrigger>
      {/* Conditionally render to work around Radix's lack of controlled 'open' prop */}
      {isOpen && (
        <ContextMenuContent className="w-[240px] p-2">
          {items.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-4">
              No unassigned items
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 max-h-[360px] overflow-y-auto">
              {items.map((itemId) => (
                <DraggableContextMenuItem
                  key={itemId}
                  id={itemId}
                  imageUrl={imageUrls[itemId] || ""}
                  name={itemNames[itemId] || ""}
                  onDragStart={() => setIsOpen(false)}
                  onClick={() => {
                    onItemSelect(itemId);
                    setIsOpen(false);
                  }}
                />
              ))}
            </div>
          )}
        </ContextMenuContent>
      )}
    </ContextMenu>
  );
}
